<template>
  <van-popup v-model:show="visible" position="bottom" round :style="{ height: '70vh' }">
    <div class="account-tree-popup">
      <div class="account-tree-header">
        <span class="account-tree-title">Select Account</span>
        <van-icon name="cross" class="account-tree-close" @click="visible = false" />
      </div>
      <van-search v-model="search" placeholder="Search account" shape="round" />
      <div v-if="loading" class="account-tree-loading">
        <van-loading type="spinner" />
      </div>
      <div v-else class="account-tree-body">
        <div
          v-for="row in visibleRows"
          :key="row.key"
          class="account-tree-row"
          :class="{
            disabled: row.disabled,
            highlighted: highlightIds.has(String(row.nodeId)),
            branch: row.isBranch,
          }"
          :style="{ paddingLeft: `${12 + row.depth * 16}px` }"
          @click="onRowClick(row)"
        >
          <van-icon
            v-if="row.isBranch"
            :name="expanded.has(row.key) ? 'arrow-down' : 'arrow'"
            class="account-tree-arrow"
          />
          <span v-else class="account-tree-dot" />
          <span class="account-tree-label">{{ row.title }}</span>
        </div>
        <van-empty v-if="!visibleRows.length" description="No Data" />
      </div>
    </div>
  </van-popup>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { getAccountList } from '@/api/detail'
import {
  buildAccountGroupTree,
  flattenVisibleAccountTree,
  getAccountHighlightIds,
  getAccountNodeTitle,
  getAccountAncestorIds,
  normalizeAccountListResponse,
  resolveAccountSelection,
  type AccountTreeNode,
  type AccountTreeRow,
} from '@/utils/account-tree'

const props = defineProps<{
  show: boolean
  account?: string
  accountGroup?: string
  accountId?: string | number | null
}>()

const emit = defineEmits<{
  'update:show': [value: boolean]
  confirm: [payload: { account: string; accountId: string | number; node: AccountTreeNode }]
}>()

const visible = computed({
  get: () => props.show,
  set: (val) => emit('update:show', val),
})

const loading = ref(false)
const search = ref('')
const rawNodes = ref<AccountTreeNode[]>([])
const expanded = ref<Set<string>>(new Set())
const selectedId = ref<string | number | ''>('')

const treeData = computed(() =>
  buildAccountGroupTree(rawNodes.value, [], { leafOnly: true, disableInactive: true }),
)

const visibleRows = computed(() =>
  flattenVisibleAccountTree(treeData.value, expanded.value, rawNodes.value, search.value),
)

const highlightIds = computed(() => getAccountHighlightIds(rawNodes.value, selectedId.value))

const syncExpandedToSelection = (id: string | number | '') => {
  if (id === '' || id == null) return
  const ancestorIds = getAccountAncestorIds(rawNodes.value, id)
  expanded.value = new Set(ancestorIds.map((item) => String(item)))
}

const syncSelectedFromProps = () => {
  const node = resolveAccountSelection(
    rawNodes.value,
    props.account,
    props.accountGroup,
    props.accountId,
  )
  selectedId.value = node?.id ?? ''
  syncExpandedToSelection(selectedId.value)
}

const loadAccountList = async () => {
  loading.value = true
  try {
    const res: any = await getAccountList()
    rawNodes.value = normalizeAccountListResponse(res?.data)
    syncSelectedFromProps()
  } catch {
    rawNodes.value = []
  } finally {
    loading.value = false
  }
}

watch(
  () => props.show,
  (show) => {
    if (!show) return
    search.value = ''
    expanded.value = new Set()
    void loadAccountList()
  },
)

const onRowClick = (row: AccountTreeRow) => {
  if (row.isBranch) {
    const next = new Set(expanded.value)
    if (next.has(row.key)) next.delete(row.key)
    else next.add(row.key)
    expanded.value = next
    return
  }
  if (!row.selectable || row.disabled) return
  selectedId.value = row.nodeId
  syncExpandedToSelection(row.nodeId)
  emit('confirm', {
    account: getAccountNodeTitle(row.rawNode),
    accountId: row.nodeId,
    node: row.rawNode,
  })
  visible.value = false
}
</script>

<style scoped lang="scss">
.account-tree-popup {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.account-tree-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px 4px;
}

.account-tree-title {
  font-size: 16px;
  font-weight: 600;
}

.account-tree-close {
  font-size: 18px;
  color: #969799;
}

.account-tree-loading {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.account-tree-body {
  flex: 1;
  overflow-y: auto;
  padding-bottom: 16px;
}

.account-tree-row {
  display: flex;
  align-items: center;
  min-height: 44px;
  padding-right: 16px;
  font-size: 14px;
}

.account-tree-row.highlighted .account-tree-label {
  color: #6e010f;
  font-weight: 600;
}

.account-tree-row.highlighted .account-tree-arrow {
  color: #6e010f;
}

.account-tree-row.highlighted .account-tree-dot {
  background: #6e010f;
}

.account-tree-row.disabled {
  color: #c8c9cc;
}

.account-tree-arrow,
.account-tree-dot {
  flex-shrink: 0;
  margin-right: 8px;
}

.account-tree-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #c8c9cc;
}

.account-tree-label {
  flex: 1;
  word-break: break-word;
}
</style>
